import { Link } from 'react-router-dom';
import { Button } from '@/src/modules/components/Button';
import { TemplateCardPreview } from '@/src/modules/renderer/TemplateCardPreview';
import { formatPrice } from '@/src/infrastructure/lib/currency';
import { useSelectProduct } from '@/src/modules/catalog/useSelectProduct';
import { useCatalogPricingMode } from '@/src/modules/catalog/useCatalogPricingMode';
import type { Collection } from '@/src/modules/catalog/types';

export function ProductCard({ product }: { product: Collection }) {
  const selectProduct = useSelectProduct();
  const { isCreditMode, isPartnerUser, remainingCredits, walletBalance, balanceFormatted } = useCatalogPricingMode();
  const creditCost = Math.round(product.amountInPaise / 100);

  return (
    <div className="group flex flex-col overflow-hidden rounded-[2rem] border border-outline-variant/30 bg-surface shadow-sm transition-shadow hover:shadow-lg">
      {/* The preview renders the template's own watermark <a>, so the detail
          link is an overlay sibling, never a wrapper (nested <a> is invalid
          HTML and a React hydration error). */}
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-surface-low">
        <TemplateCardPreview template={product.template} />
        <Link
          to={`/catalog/${product.id}`}
          aria-label={`View ${product.displayName}`}
          className="absolute inset-0 z-10"
        />
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <h3 className="text-lg font-bold text-on-surface line-clamp-1">{product.displayName}</h3>
          {product.description && (
            <p className="mt-1 text-sm text-on-surface-variant line-clamp-2">{product.description}</p>
          )}
        </div>

        <div className="mt-auto flex items-center justify-between gap-3 pt-2">
          {isCreditMode ? (
            <div className="flex items-center gap-1.5" data-testid="credit-price-indicator">
              <span className="text-sm font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                ₹{creditCost.toLocaleString('en-IN')}
              </span>
              {isPartnerUser && (walletBalance !== undefined ? (walletBalance > 0 || remainingCredits > 0) : remainingCredits > 0) && (
                <span className="text-xs text-on-surface-variant font-medium">
                  ({balanceFormatted ? `${balanceFormatted} left` : walletBalance !== undefined && walletBalance > 0 ? `₹${walletBalance.toLocaleString('en-IN')} left` : `${remainingCredits} left`})
                </span>
              )}
            </div>
          ) : (
            <span className="text-xl font-bold text-on-surface">
              {formatPrice(product.amountInPaise, product.currency)}
            </span>
          )}
          <Button variant="primary" size="sm" onClick={() => selectProduct(product)}>
            {isCreditMode ? 'Use Template' : 'Select'}
          </Button>
        </div>
      </div>
    </div>
  );
}
